import { getAuthToken } from './api';

// API base URL - must match api.ts for consistent routing
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/questions';

export type ExportFormat = 'csv' | 'excel';

export type ExportColumn =
  | 'id'
  | 'timestamp'
  | 'question_text'
  | 'answer_text'
  | 'status'
  | 'source'
  | 'asker_name'
  | 'category'
  | 'tags'
  | 'assigned_doctor'
  | 'answered_by'
  | 'answered_at';

export interface ExportOptions {
  format: ExportFormat;
  dateFrom?: string;
  dateTo?: string;
  columns?: ExportColumn[];
  status?: string[];
  source?: string[];
}

export const EXPORT_COLUMNS: { value: ExportColumn; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'timestamp', label: 'Date' },
  { value: 'question_text', label: 'Question' },
  { value: 'answer_text', label: 'Answer' },
  { value: 'status', label: 'Status' },
  { value: 'source', label: 'Source' },
  { value: 'asker_name', label: 'Asker' },
  { value: 'category', label: 'Category' },
  { value: 'tags', label: 'Tags' },
  { value: 'assigned_doctor', label: 'Assigned To' },
  { value: 'answered_by', label: 'Answered By' },
  { value: 'answered_at', label: 'Answered At' },
];

export const DEFAULT_COLUMNS: ExportColumn[] = [
  'id',
  'timestamp',
  'question_text',
  'answer_text',
  'status',
  'source',
  'asker_name',
  'category',
  'answered_by',
  'answered_at',
];

/**
 * Export questions in the specified format
 */
export async function exportQuestions(options: ExportOptions): Promise<Blob> {
  const { format, ...body } = options;
  const endpoint = format === 'csv' ? `${API_BASE}/export/csv` : `${API_BASE}/export/excel`;
  const token = getAuthToken();

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Export failed' }));
    throw new Error(error.error || 'Export failed');
  }

  return response.blob();
}

/**
 * Trigger browser download of blob
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate filename for export
 */
export function getExportFilename(format: ExportFormat): string {
  const date = new Date().toISOString().split('T')[0];
  const ext = format === 'csv' ? 'csv' : 'xlsx';
  return `questions-export-${date}.${ext}`;
}
